import { useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Archive, ArchiveRestore, Trash2, PauseCircle, PlayCircle, ShieldCheck, X, TriangleAlert } from "lucide-react";
import Button from "../../ui/Button";
import renderInput from "../../form/renderInput";
import useEscapeKey from "../../../_hooks/useEscapeKey";
import "./_confirm-dialog.scss";

// Ported from src/components/ConfirmationModal's MODAL_CONFIG - only the 11
// types with real, live consumers (confirmed via a full-codebase audit).
// `activate`/`deactivate`/`logout` were in the old table but never actually
// invoked by any real caller - not carried forward. Colors/tones match the
// old component's own global stylesheet (src/assets/scss/components/
// _confirmation-modal.scss) exactly. `writeoff`/`cancel` never had their own
// color there either (fell through to the unstyled base circle) - "neutral"
// here is the closest faithful match, not a new visual invented for them.
const TYPE_PRESETS = {
  archive: { icon: Archive, tone: "neutral", title: "Archive", confirmText: "Yes, archive", getDescription: (d) => d || "Are you sure you want to archive?" },
  unarchive: { icon: ArchiveRestore, tone: "neutral", title: "Unarchive", confirmText: "Yes, unarchive", getDescription: (d) => d || "Are you sure you want to unarchive?" },
  delete: { icon: Trash2, tone: "danger", title: "Delete", confirmText: "Yes, delete", getDescription: (d) => d || "Are you sure you want to delete this item? Once deleted, it cannot be restored." },
  suspend: { icon: PauseCircle, tone: "warning", title: "Suspend", confirmText: "Yes, suspend", getDescription: (d) => d || "Are you sure you want to suspend?" },
  unsuspend: { icon: PlayCircle, tone: "success", title: "Unsuspend", confirmText: "Yes, unsuspend", getDescription: (d) => d || "Are you sure you want to unsuspend?" },
  resend: { icon: ShieldCheck, tone: "info", title: "Resend", confirmText: "Yes, resend", getDescription: (d) => d || "Are you sure you want to resend?" },
  confirm: { icon: ShieldCheck, tone: "info", title: "Confirm", confirmText: "Confirm", getDescription: (d) => d || "Please confirm the action." },
  resendInvitation: { icon: ShieldCheck, tone: "info", title: "Resend Invitation", confirmText: "Yes, resend", getDescription: (d) => d || "Are you sure you want to resend the invitation?" },
  writeoff: { icon: Archive, tone: "neutral", title: "Write off", confirmText: "Yes, write off", getDescription: (d) => d || "Are you sure you want to write off this invoice?" },
  cancel: { icon: X, tone: "neutral", title: "Cancel", confirmText: "Yes, cancel", getDescription: (d) => d || "Are you sure you want to cancel?" },
  warning: { icon: TriangleAlert, tone: "warning", title: "Warning", confirmText: "Yes, proceed", getDescription: (d) => d || "This action cannot be undone. Are you sure you want to proceed?" },
};

const isFieldValid = (field, formData, errors) => {
  if (!field.required) return true;
  const value = formData[field.name];
  if (errors?.[field.name]) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true;
  if (typeof value === "number") return !isNaN(value);
  return value !== null && value !== undefined && String(value).trim() !== "";
};

/**
 * "Are you sure" confirmation dialog. Two modes, both supported by the same
 * component:
 *
 * - No `type`: a plain, single-icon dialog (today's original shape,
 *   unchanged - `title`/`description` are required, defaults stay the
 *   "Keep editing"/"Discard changes" unsaved-changes framing this component
 *   was originally built for).
 * - `type` given (one of TYPE_PRESETS' keys): supplies a default icon +
 *   color + title + description + confirm text, absorbing what
 *   src/components/ConfirmationModal used to do - `title`/`description`/
 *   `confirmText` still work as explicit overrides on top of the preset,
 *   same as that component's own `desc || cfg.getDesc(desc)` pattern.
 *   `cancelText` defaults to "Cancel" in this mode (that component never
 *   varied it by type).
 *
 * `inputSchema` (+ `formData`/`setFormData`/`errors`/`handleBlur`/
 * `setImageFile`/`setShouldDeleteImage`): renders an embedded dynamic form
 * via the shared `_components/form/renderInput` bridge between the
 * description and the action buttons - ported from ConfirmationModal's own
 * embedded-form support (used by exactly 2 real flows: suspend's single
 * reason-select, writeoff's reason-textarea + notify-checkbox). The confirm
 * button is disabled until every required field is filled, same as before.
 *
 * Own portal so it can stack above a ModalShell. ESC maps to Cancel.
 * Deliberately no overlay-click-to-close - confirming/discarding should
 * take an explicit button press.
 */
const ConfirmDialog = ({
  isOpen,
  type,
  title,
  description,
  cancelText,
  confirmText,
  onCancel,
  onConfirm,
  isLoading = false,
  inputSchema = [],
  formData = {},
  setFormData,
  errors = {},
  handleBlur,
  setImageFile,
  setShouldDeleteImage,
}) => {
  useEscapeKey(isOpen, () => {
    if (!isLoading) onCancel();
  });

  const handleChange = useCallback(
    (name, value) => setFormData?.((prev) => ({ ...prev, [name]: value })),
    [setFormData]
  );

  const isFormValid = useMemo(
    () => inputSchema.every((field) => isFieldValid(field, formData, errors)),
    [inputSchema, formData, errors]
  );

  // A disabled confirm button gives no reason on its own; name what's missing.
  const missingLabels = useMemo(
    () => inputSchema.filter((field) => !isFieldValid(field, formData, errors)).map((field) => field.label || field.name),
    [inputSchema, formData, errors]
  );

  if (!isOpen) return null;

  const preset = type ? TYPE_PRESETS[type] : null;
  const Icon = preset?.icon ?? TriangleAlert;
  const resolvedTitle = title ?? preset?.title;
  const resolvedDescription = preset ? preset.getDescription(description) : description;
  const resolvedConfirmText = confirmText ?? preset?.confirmText ?? "Discard changes";
  const resolvedCancelText = cancelText ?? (preset ? "Cancel" : "Keep editing");
  const isConfirmDisabled = isLoading || !isFormValid;

  return createPortal(
    <div className="confirm-dialog__overlay">
      <div
        className="confirm-dialog__content"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className={`confirm-dialog__icon ${preset ? `confirm-dialog__icon--${preset.tone}` : ""}`}>
          <Icon size={20} />
        </div>

        <h2 id="confirm-dialog-title" className="confirm-dialog__title">
          {resolvedTitle}
        </h2>

        {resolvedDescription && <p className="confirm-dialog__description">{resolvedDescription}</p>}

        {inputSchema.length > 0 && (
          <div className="confirm-dialog__form">
            {inputSchema.map((field) =>
              renderInput(
                field,
                formData,
                errors,
                handleChange,
                handleBlur,
                setImageFile,
                setShouldDeleteImage,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                isLoading
              )
            )}
          </div>
        )}

        {!isLoading && missingLabels.length > 0 && (
          <p className="confirm-dialog__hint" role="status">
            {`Required: ${missingLabels.join(", ")}`}
          </p>
        )}

        <div className="confirm-dialog__actions">
          <Button variant="outlined-neutral" onClick={onCancel} disabled={isLoading}>
            {resolvedCancelText}
          </Button>
          <Button
            variant={preset?.tone === "danger" ? "contained-danger" : "contained"}
            onClick={onConfirm}
            loading={isLoading}
            disabled={isConfirmDisabled}
          >
            {resolvedConfirmText}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
